import { FACE_PILE_LIMIT } from './design/facePile';
import { ProductNotice, SharingDetails, SPLIT_REMINDER, PAYMENT_RECORD_NOTICE, SplitRoundingNotice } from './components/ProductNotice';
import { requestKey, money } from './payments/contracts';
import PaymentReview from './payments/PaymentReview';
import { sharedPaymentClient, paymentFeatures } from './payments/client';
import usePaymentReturn, { navigateToPaymentApp, clearPaymentReturn, paymentAppOpensInNewTab } from "./hooks/usePaymentReturn";
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PublicBillView.css";
import "./design/tokens.css";
import "./design/public-bill.css";
import PublicBillDialog from "./components/PublicBillDialog";
import SettledCelebration from './components/SettledCelebration';
import PersonAvatar, { avatarStyle } from "./components/PersonAvatar";
import BillPaymentPeople, { BillPaymentPerson } from "./components/BillPaymentPeople";
import { Copy, ChevronRight, Check, ReceiptText, ArrowDownRight } from "lucide-react";
import { usesMobileShareSheet, shareBrowserLink } from './lib/browserShare';
import PublicAppHeader from "./components/PublicAppHeader";
import { OpenInAppRow } from "./components/OpenInApp";
import BillMainDetails from "./components/BillMainDetails";
import { PaymentReturnContent } from "./components/PaymentConfirmationModal";
import PayHandoffModal, {
  PayHandoffRecipient,
  PayHandoffPayingFor,
  PayHandoffReminder,
} from "./components/PayHandoffModal";
import config from "./config";
import {
  formatMoney,
  normalizeCurrency,
  type SupportedCurrency,
} from "./lib/currency";
import { RiErrorWarningLine, RiLoader4Line, RiShareForwardLine } from "react-icons/ri";
import { QRCodeSVG } from "qrcode.react";

const ShareForwardIcon = RiShareForwardLine as unknown as React.ComponentType<{ size?: number; className?: string }>;
const PayerWarningIcon = RiErrorWarningLine as unknown as React.ComponentType<{ size?: number; className?: string }>;

const LoaderIcon = RiLoader4Line as unknown as React.ComponentType<{
  size?: number;
  className?: string;
}>;

interface Addon {
  name: string;
  price: number;
}

interface LineItem {
  id: string;
  description: string;
  amount: number;
  quantity?: number; // Quantity for multi-item entries
  assignedTo: string[];
  addons?: Addon[];
}

interface Participant {
  id: string;
  name: string;
  username?: string;
  type: "registered" | "known" | "guest" | "deleted";
  profilePictureUrl?: string;
  venmoProfilePictureOriginalUrl?: string;
  linkedUserId?: string; // For guests linked to known users
  venmoUsername?: string;
  venmoDisplayName?: string;
  upiId?: string;
  upiVpa?: string;
  upiHandle?: string;
  upi?: string;
}

interface ParticipantShare {
  participantId: string;
  participantName: string;
  participantType: "registered" | "known" | "guest" | "deleted";
  items: LineItem[];
  itemsSubtotal: number;
  taxShare: number;
  tipShare: number;
  surcharges?: { [key: string]: number }; // Individual surcharge breakdown
  surchargeShare: number; // Total surcharges
  discounts?: { [key: string]: number }; // Individual discount breakdown
  discountShare: number; // Total discounts
  total: number;
  paymentStatus: "pending" | "venmo_requested" | "pending_confirmation" | "paid";
  requestedAt?: string | null;
  paidAt?: string | null;
  selfMarkedPaidAt?: string | null;
}

interface Bill {
  id: string;
  splitType?: string;
  title: string;
  date: string;
  total: number;
  subtotal: number;
  tax: number;
  tip: number;
  items: LineItem[];
  participants: Participant[];
  participantShares: ParticipantShare[];
  status: "editing" | "pending" | "settled";
  receiptImageUrls?: string[] | null; // Manual bills may have no receipt.
  creator?: string;
  userId?: string; // Creator's user ID
  paidBy?: string; // User ID of who actually paid (defaults to creator/userId)
  emoji?: string; // Bill emoji
  surcharges?: { [key: string]: number };
  discounts?: { [key: string]: number };
  requirePaymentConfirmation?: boolean;
  currency?: SupportedCurrency;
  notes?: string;
  // Whether the payer can be paid from this link, from their user record (#175).
  payerReceiving?: { userId: string | null; ready: boolean; currencies: string[] } | null;
}

// Recurring instances store each share as `{ participantId, total, paymentStatus… }`
// (services/recurringValidation.js strips everything else), while itemized bills
// also carry the person's name, type and items on the share. GET /bills/shared
// returns the stored shares as-is, so fill the gaps from `participants` here —
// every row, avatar, aria-label and dialog title reads from the share. #51
export function normalizeSharedBill(data: Bill): Bill {
  const participants = data.participants || [];
  return {
    ...data,
    items: data.items || [],
    participants,
    participantShares: (data.participantShares || []).map((share) => {
      const person = participants.find((candidate) => candidate.id === share.participantId);
      return {
        ...share,
        participantName: share.participantName || person?.name || 'Guest',
        participantType: share.participantType || person?.type || 'guest',
        items: share.items || [],
      };
    }),
  };
}

export default function PublicBillView() {
  // Extract shareId from URL path (/b/:shareId)
  const shareId = window.location.pathname.split("/b/")[1];
  const [bill, setBill] = useState<Bill | null>(null);
  // Don't show the legacy Venmo-only flow while configuration is loading or
  // unavailable. The connector endpoint checks availability and exposes retry.
  const [paymentsV2, setPaymentsV2] = useState(true);
  const paymentClient = React.useMemo(() => sharedPaymentClient({ kind: 'bill', code: shareId }), [shareId]);
  useEffect(() => {
    let live = true;
    setPaymentsV2(true);
    void paymentFeatures().then(features => { if (live) setPaymentsV2(features.repayment); }).catch(() => { /* Payment review remains authoritative and retryable. */ });
    return () => { live = false; paymentClient.clear(); };
  }, [paymentClient]);
  const [isLoading, setIsLoading] = useState(true);
  const [shareFeedback, setShareFeedback] = useState("");
  const [shareFailed, setShareFailed] = useState(false);
  const [sharing, setSharing] = useState(false);
  const sharingRef = React.useRef(false);
  const mobileShare = usesMobileShareSheet();
  const [error, setError] = useState<string | null>(null);
  // The details modal tracks the open participant by ID and derives the share
  // from `bill` each render — storing the share object itself would be a stale
  // snapshot after mark paid/unpaid reloads the bill.
  const [activeParticipantId, setActiveParticipantId] = useState<string | null>(null);
  const [detailParticipantIds, setDetailParticipantIds] = useState<string[]>([]);
  const [editingDetailPeople, setEditingDetailPeople] = useState(false);
  const [detailDraftIds, setDetailDraftIds] = useState<string[]>([]);
  const [detailUpiScheme, setDetailUpiScheme] = useState('');


  const [showParticipantDetailsModal, setShowParticipantDetailsModal] =
    useState(false);
  useEffect(() => {
    setDetailUpiScheme('');
    setEditingDetailPeople(false);
    setDetailDraftIds([]);
  }, [activeParticipantId, showParticipantDetailsModal]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareBillModal, setShowShareBillModal] = useState(false);
  const [showUpiAppPickerModal, setShowUpiAppPickerModal] = useState(false);
  const [upiHandoffApp, setUpiHandoffApp] = useState<{ label: string; scheme: string } | null>(null);
  useEffect(() => { setUpiHandoffApp(null); }, [showUpiAppPickerModal]);
  const [pendingUpiPayment, setPendingUpiPayment] = useState<{
    payerUpiId: string;
    payerName: string;
    payerAvatarUrl?: string;
    totalAmount: number;
    noteText: string;
    participantIds: string[];
    payingFor: { label: string; avatarUrl?: string; amountLabel?: string }[];
  } | null>(null);
  // Venmo handoff interstitial: the resolved participant ids awaiting the
  // user's "Continue to Venmo" — non-null means the dialog is open.
  const [venmoHandoffIds, setVenmoHandoffIds] = useState<string[] | null>(null);

  // Multi-select payment states
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<
    string[]
  >([]);
  const [paymentReviewBusy, setPaymentReviewBusy] = useState(false);
  // Settled confirmation after shares are marked as paid: celebrate first, then invite the payer to the app.
  const [settled, setSettled] = useState<{ amountLabel: string; detail?: string } | null>(null);
  const [markingParticipantId, setMarkingParticipantId] = useState<string | null>(null);

  // Venmo payment dialog states
  const [showVenmoDialog, setShowVenmoDialog] = useState(false);
  const [isConfirmingPaymentComplete, setIsConfirmingPaymentComplete] = useState(false);
  const [pendingVenmoConfirmationOnFocus, setPendingVenmoConfirmationOnFocus] =
    useState(false);
  const [pendingUpiModalCloseOnFocus, setPendingUpiModalCloseOnFocus] =
    useState(false);


  const paymentOrigin = React.useRef<'details' | 'standalone' | 'upi'>('standalone');
  usePaymentReturn<string[] | { ids: string[]; origin: 'details' | 'standalone' | 'upi' }>((saved) => {
    const ids = Array.isArray(saved) ? saved : saved.ids;
    const origin = Array.isArray(saved) ? 'standalone' : saved.origin;
    paymentOrigin.current = origin;
    if (origin === 'details') {
      setActiveParticipantId(ids[0]);
      setDetailParticipantIds(ids);
      setEditingDetailPeople(false);
      setShowParticipantDetailsModal(true);
    } else if (origin === 'upi' && pendingUpiPayment) {
      setShowUpiAppPickerModal(true);
    } else {
      setVenmoHandoffIds(ids);
    }
    setSelectedParticipantIds(ids);
    setShowVenmoDialog(true);
    setPendingVenmoConfirmationOnFocus(false);
  }, !!bill);

  const getParticipantUpiId = (participant?: Participant | null) =>
    participant?.upiId ||
    participant?.upiVpa ||
    participant?.upiHandle ||
    participant?.upi;

  // Helper function to format snake_case or camelCase to Title Case
  const formatLabel = (key: string): string => {
    return key
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/([A-Z])/g, " $1") // Add space before capital letters (for camelCase)
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
      .trim();
  };

  useEffect(() => {
    loadBill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);
  // Exchange the link for payment access as soon as the bill is on the page, so tapping a
  // share never waits on that round trip. The share sheet itself renders from `bill` (#52).
  const billId = bill?.id;
  useEffect(() => {
    if (billId && paymentsV2) paymentClient.warm();
  }, [billId, paymentsV2, paymentClient]);

  const loadBill = async () => {
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/bills/shared/${shareId}`
      );
      setError(null);
      setBill(normalizeSharedBill(response.data));
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error loading bill:", error);
      setError(error.response?.data?.error || "Bill not found");
      setIsLoading(false);
    }
  };

  const getStatusSummary = () => {
    if (!bill || !bill.participantShares) {
      return {
        pending: [],
        venmoRequested: [],
        pendingConfirmation: [],
        paid: [],
        pendingAmount: 0,
        creatorId: null,
        creatorFirstName: 'creator',
        payerId: null,
        payerFirstName: 'payer',
      };
    }

    // Use bill.userId as the authoritative creator ID
    const creatorId = bill.userId || null;

    // Payer is who actually paid (defaults to creator)
    const payerId = bill.paidBy || bill.userId || null;

    // Get creator's first name from participants
    const creatorParticipant = bill.participants?.find((p) => p.id === creatorId);
    const creatorFirstName = creatorParticipant?.name?.split(' ')[0] || 'creator';

    // Get payer's first name from participants
    const payerParticipant = bill.participants?.find((p) => p.id === payerId);
    const payerFirstName = payerParticipant?.name?.split(' ')[0] || 'payer';

    // Payer is always paid (they don't owe themselves), exclude from pending lists
    const pending = bill.participantShares.filter(
      (s) => s.paymentStatus === "pending" && s.participantId !== payerId
    );
    const venmoRequested = bill.participantShares.filter(
      (s) =>
        s.paymentStatus === "venmo_requested" && s.participantId !== payerId
    );
    const pendingConfirmation = bill.participantShares.filter(
      (s) =>
        s.paymentStatus === "pending_confirmation" && s.participantId !== payerId
    );

    // Include payer in paid list if they exist (they already paid)
    const paid = bill.participantShares.filter(
      (s) => s.paymentStatus === "paid" || s.participantId === payerId
    );

    const pendingAmount =
      bill.status === "settled"
        ? 0
        : [...pending, ...venmoRequested].reduce((sum, s) => sum + s.total, 0);

    return { pending, venmoRequested, pendingConfirmation, paid, pendingAmount, creatorId, creatorFirstName, payerId, payerFirstName };
  };

  // Pay helpers accept the review selection, or the saved selection when
  // confirming payment after returning from the provider.
  const getSelectedParticipantIdsForPay = (participantIds?: string[]) => {
    if (!bill) return [];
    const payerId = bill.paidBy || bill.userId;
    const candidateIds = participantIds ?? selectedParticipantIds;
    return bill.participantShares
      .filter((share) => candidateIds.includes(share.participantId))
      .filter((share) => {
        const isPayer = share.participantId === payerId;
        const isPaid = share.paymentStatus === "paid";
        const isPendingConfirmation = share.paymentStatus === "pending_confirmation";
        const owesNothing = !share.total || share.total <= 0;
        return !isPayer && !isPaid && !isPendingConfirmation && !owesNothing;
      })
      .map((share) => share.participantId);
  };

  const getSelectedTotalForPay = (participantIds?: string[]) => {
    if (!bill) return 0;
    const payIds = new Set(getSelectedParticipantIdsForPay(participantIds));
    return bill.participantShares
      .filter((share) => payIds.has(share.participantId))
      .reduce((sum, share) => sum + share.total, 0);
  };

  // Payer lookup shared by the Venmo/UPI flows and the details-modal Pay
  // button: by id first, then creator name, then first registered user.
  const resolvePayerParticipant = () => {
    if (!bill) return undefined;
    const payerId = bill.paidBy || bill.userId;
    return (
      bill.participants.find((p) => p.id === payerId) ||
      bill.participants.find((p) => p.name === bill.creator) ||
      bill.participants.find((p) => p.type === "registered")
    );
  };

  // Launch after acknowledgement; preserve selection for browser Back.
  const handleContinueVenmoHandoff = () => {
    const ids = venmoHandoffIds;
    if (!ids) return;
    return proceedToVenmo(ids);
  };

  const handlePayWithUpi = (participantIds?: string[], appScheme?: string) => {
    if (!bill) return;
    const participantIdsForPay = getSelectedParticipantIdsForPay(participantIds);
    if (participantIdsForPay.length === 0) return;
    if (paymentsV2) { setVenmoHandoffIds(participantIdsForPay); return; }

    const payerParticipant = resolvePayerParticipant();

    const payerUpiId = getParticipantUpiId(payerParticipant);
    if (!payerUpiId) {
      alert("Bill payer has not set up UPI yet");
      return;
    }

    const selectedNames = participantIdsForPay
      .map((id) => bill.participants.find((p) => p.id === id)?.name?.split(" ")[0])
      .filter(Boolean)
      .join(", ");

    const noteText = `${bill.title} for ${selectedNames} (Split by VibeSplit)`;

    const payment = {
      payerUpiId,
      payerName: payerParticipant?.name || "Payer",
      payerAvatarUrl: payerParticipant?.profilePictureUrl,
      totalAmount: getSelectedTotalForPay(participantIds),
      noteText,
      participantIds: participantIdsForPay,
      payingFor: participantIdsForPay.map((id) => {
        const participant = bill.participants.find((p) => p.id === id);
        return {
          label: participant?.name?.split(" ")[0] || "Guest",
          avatarUrl: participant?.profilePictureUrl,
          amountLabel: formatMoney(bill.participantShares.find((share) => share.participantId === id)?.total || 0, bill.currency),
        };
      }),
    };
    if (appScheme) {
      handleSelectUpiApp(appScheme, payment);
    } else {
      setPendingUpiPayment(payment);
      setShowUpiAppPickerModal(true);
    }
  };

  const handleSelectUpiApp = (baseUrl: string, payment = pendingUpiPayment) => {
    if (!payment) return;

    const amount = Number(payment.totalAmount || 0).toFixed(2);
    const upiUrl = `${baseUrl}?pa=${encodeURIComponent(
      payment.payerUpiId
    )}&pn=${encodeURIComponent(
      payment.payerName
    )}&am=${amount}&cu=INR&tn=${encodeURIComponent(payment.noteText)}`;

    navigateToPaymentApp(upiUrl, { ids: payment.participantIds, origin: paymentOrigin.current });
    setPendingUpiModalCloseOnFocus(true);
    setPendingVenmoConfirmationOnFocus(true);
  };

  const upiAppOptions = [
    {
      label: "Google Pay",
      scheme: "tez://upi/pay",
      logoUrl: "https://commons.wikimedia.org/wiki/Special:FilePath/Google%20Pay%20Logo.svg",
    },
    {
      label: "PhonePe",
      scheme: "phonepe://pay",
      logoUrl: "https://commons.wikimedia.org/wiki/Special:FilePath/PhonePe%20Logo.svg",
    },
    {
      label: "Paytm",
      scheme: "paytmmp://pay",
      logoUrl: "https://commons.wikimedia.org/wiki/Special:FilePath/Paytm%20Logo%20%28standalone%29.svg",
    },
    {
      label: "BHIM",
      scheme: "bhim://upi/pay",
      logoUrl: "https://commons.wikimedia.org/wiki/Special:FilePath/Bhim%20app%20logo.svg",
    },
    {
      label: "Other",
      scheme: "upi://pay",
      logoUrl: "https://commons.wikimedia.org/wiki/Special:FilePath/UPI%20logo.svg",
    },
  ] as const;

  const proceedToVenmo = (participantIds?: string[]) => {
    if (!bill) return;
    const participantIdsForPay = getSelectedParticipantIdsForPay(participantIds);
    if (participantIdsForPay.length === 0) return;
    if (paymentsV2) { setVenmoHandoffIds(participantIdsForPay); return; }

    // Payer is who actually paid (defaults to creator/userId)
    const payerParticipant = resolvePayerParticipant();

    // Check for Venmo username in order of preference
    const payerVenmo =
      payerParticipant?.venmoUsername;

    console.log("Payer participant:", payerParticipant);
    console.log("Payer venmo username:", payerVenmo);

    if (!payerVenmo) {
      alert("Bill payer has not set up Venmo yet");
      setShowVenmoDialog(false);
      return;
    }

    // Get selected participant first names only
    const selectedNames = participantIdsForPay
      .map((id) => {
        const participant = bill.participants.find((p) => p.id === id);
        // Get first name only (split by space and take first part)
        return participant?.name?.split(" ")[0];
      })
      .filter(Boolean)
      .join(", ");

    const total = getSelectedTotalForPay(participantIds);
    const cleanUsername = payerVenmo.replace(/^@/, "");
    const amount = total.toFixed(2);

    // Format note: "Bill Title for FirstName, FirstName (Split by VibeSplit)"
    const noteText = `${bill.title} for ${selectedNames} (Split by VibeSplit)`;

    const note = encodeURIComponent(noteText);

    const venmoWebLink = `https://venmo.com/${cleanUsername}?txn=pay&amount=${amount}&note=${note}`;

    const target = navigateToPaymentApp(venmoWebLink, { ids: participantIdsForPay, origin: paymentOrigin.current });
    // Prepare the original tab immediately, including browsers that open tabs
    // in the background. Mobile still waits for the return from the app.
    setShowVenmoDialog(target === 'new-tab');
    setPendingVenmoConfirmationOnFocus(target !== 'new-tab');
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleWindowFocus = () => {
      if (pendingUpiModalCloseOnFocus) {
        setPendingUpiModalCloseOnFocus(false);
      }
      if (pendingVenmoConfirmationOnFocus) {
        setShowVenmoDialog(true);
        setPendingVenmoConfirmationOnFocus(false);
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [pendingVenmoConfirmationOnFocus, pendingUpiModalCloseOnFocus]);

  const handleConfirmPaymentComplete = async (ids?: string[]) => {
    if (!bill) return;
    const participantIdsForPay = getSelectedParticipantIdsForPay(ids);
    if (participantIdsForPay.length === 0) return;
    if (paymentsV2) { setVenmoHandoffIds(participantIdsForPay); return; }

    setIsConfirmingPaymentComplete(true);
    try {
      // Use batch endpoint to mark all participants as paid with a single notification
      await axios.put(
        `${config.apiBaseUrl}/bills/${bill.id}/participant-shares/batch-mark-paid-public`,
        {
          participantIds: participantIdsForPay,
          shareId
        }
      );
      celebrate(participantIdsForPay);
      setShowParticipantDetailsModal(false);
      await loadBill();
      handleCloseVenmoDialog();
    } catch (error: any) {
      console.error("Error marking payment as complete:", error);
      alert(error?.response?.data?.error || "Failed to confirm payment");
    } finally {
      setIsConfirmingPaymentComplete(false);
    }
  };

  // Measured before the reload, so the amount is the shares that were just marked as paid.
  const celebrate = (ids: string[], recorded?: { amountMinor: number; currency: string }) => {
    if (!bill || !ids.length) return;
    const names = ids.map((id) => bill.participants.find((person) => person.id === id)?.name?.split(' ')[0]).filter(Boolean);
    const total = getSelectedTotalForPay(ids);
    const amountLabel = recorded ? (recorded.amountMinor > 0 ? money(recorded.amountMinor, recorded.currency) : '') : total > 0 ? formatMoney(total, bill.currency) : '';
    setSettled({ amountLabel, detail: names.length > 1 ? `For ${names.join(', ')}.` : undefined });
  };
  const handleCloseVenmoDialog = () => {
    clearPaymentReturn();
    setShowVenmoDialog(false);
    setVenmoHandoffIds(null);
    setShowUpiAppPickerModal(false);
    setPendingUpiPayment(null);
    setSelectedParticipantIds([]);
    setPendingVenmoConfirmationOnFocus(false);
  };

  const paymentReturnIds = getSelectedParticipantIdsForPay(selectedParticipantIds);
  const paymentReturnContent = <PaymentReturnContent
    busy={isConfirmingPaymentComplete}
    amountLabel={bill ? formatMoney(getSelectedTotalForPay(paymentReturnIds), bill.currency) : undefined}
    people={paymentReturnIds.map((id) => bill?.participants.find((person) => person.id === id)?.name || 'Guest')}
    onConfirm={() => handleConfirmPaymentComplete()}
    onNotYet={handleCloseVenmoDialog}
  />;

  // Per-participant mark paid / unpaid — used by the details modal (mirrors the
  // mobile app, where marking lives in the participant breakdown modal).
  const markParticipantAsPaid = async (participantId: string) => {
    if (!bill) return;
    setMarkingParticipantId(participantId);
    try {
      await axios.put(
        `${config.apiBaseUrl}/bills/${bill.id}/participant-shares/batch-mark-paid-public`,
        { participantIds: [participantId], shareId, ...(paymentsV2 ? { expectedRevision: (bill as any).revision || 0, idempotencyKey: requestKey() } : {}) },
        paymentsV2 ? { headers: { 'X-Vibesplit-Capabilities': 'payments-v2' } } : undefined
      );
      celebrate([participantId]);
      setShowParticipantDetailsModal(false);
      await loadBill();
    } catch (error: any) {
      console.error("Error marking participant as paid:", error);
      alert(error?.response?.data?.error || "Failed to mark as paid");
    } finally {
      setMarkingParticipantId(null);
    }
  };
  const markParticipantAsPending = async (participantId: string) => {
    if (!bill) return;
    setMarkingParticipantId(participantId);
    try {
      await axios.put(
        `${config.apiBaseUrl}/bills/${bill.id}/participant-shares/${participantId}/mark-pending-public`,
        { shareId, ...(paymentsV2 ? { expectedRevision: (bill as any).revision || 0, idempotencyKey: requestKey() } : {}) },
        paymentsV2 ? { headers: { 'X-Vibesplit-Capabilities': 'payments-v2' } } : undefined
      );
      setShowParticipantDetailsModal(false);
      await loadBill();
    } catch (error: any) {
      console.error("Error marking participant as not paid:", error);
      alert(error?.response?.data?.error || "Failed to mark as not paid");
    } finally {
      setMarkingParticipantId(null);
    }
  };

  const handleShareBill = async () => {
    setShowShareBillModal(true);
  };

  const handleShareLink = async () => {
    if (!bill || sharingRef.current) return;
    sharingRef.current = true;
    setSharing(true);
    setShareFailed(false);
    setShareFeedback('');
    const shareUrl = `${window.location.origin}/b/${shareId}`;
    try {
      const result = await shareBrowserLink(shareUrl);
      if (result === 'copied') setShareFeedback('Link copied');
    } catch {
      setShareFailed(true);
      setShareFeedback(mobileShare ? 'Couldn’t share. Try again or copy the link below.' : 'Couldn’t copy. Select and copy the link below.');
    } finally {
      sharingRef.current = false;
      setSharing(false);
    }
  };

  if (isLoading) {
    return <><OpenInAppRow target="b" shareId={shareId} /><div className="vs-design bill-page"><div className="bill-page__container"><PublicAppHeader showAppLink={false} />
      <main className="bill-state" role="status"><LoaderIcon className="bill-loading-icon" size={32} /><p>Loading bill…</p></main>
    </div></div></>;
  }

  if (error || !bill) {
    return <><OpenInAppRow target="b" shareId={shareId} /><div className="vs-design bill-page"><div className="bill-page__container"><PublicAppHeader showAppLink={false} />
      <main className="bill-state"><div className="bill-occasion"><ReceiptText size={28} /></div><h1>Couldn’t load this bill</h1>
        <p>{error || 'This link may have expired or the bill was deleted.'}</p>
        <button type="button" className="vs-button vs-button--primary" onClick={() => { setIsLoading(true); void loadBill(); }}>Try again</button>
      </main>
    </div></div></>;
  }

  const statusSummary = getStatusSummary();
  const payer = resolvePayerParticipant();
  const payerName = payer?.name || bill.creator || 'Unknown';
  const paymentTotals = (() => {
    const billPayerId = bill.paidBy || bill.userId;
    const relevantShares = bill.participantShares.filter((share) => share.total > 0);
    const totalDue = relevantShares.reduce((sum, share) => sum + share.total, 0);
    const totalPaid = relevantShares.reduce((sum, share) => {
      const isPayerShare = share.participantId === billPayerId;
      const isPaidShare = share.paymentStatus === "paid" || isPayerShare;
      return sum + (isPaidShare ? share.total : 0);
    }, 0);
    return { totalDue, totalPaid };
  })();
  const paymentProgressPercentage =
    paymentTotals.totalDue === 0
      ? 0
      : (paymentTotals.totalPaid / paymentTotals.totalDue) * 100;
  const hasSelectablePendingShares = getSelectedParticipantIdsForPay(
    bill.participantShares.map((share) => share.participantId)
  ).length > 0;

  // Details modal: the LIVE share for the open participant (recomputed from
  // `bill` every render so mark paid/unpaid updates the modal in place).
  const activeParticipantShare = activeParticipantId
    ? bill.participantShares.find((s) => s.participantId === (detailParticipantIds[0] || activeParticipantId)) || null
    : null;
  const detailIsPayer =
    !!activeParticipantShare &&
    activeParticipantShare.participantId === statusSummary.payerId;
  const detailOwesNothing =
    !!activeParticipantShare &&
    (!activeParticipantShare.total || activeParticipantShare.total <= 0);
  const detailPaid =
    detailIsPayer || detailOwesNothing || activeParticipantShare?.paymentStatus === "paid";
  const detailAwaiting =
    !detailPaid &&
    Boolean(bill.requirePaymentConfirmation) &&
    activeParticipantShare?.paymentStatus === "pending_confirmation";
  const detailCanFlip = detailParticipantIds.length === 1 && !!activeParticipantShare && !detailIsPayer && !detailOwesNothing;
  const detailBusy = paymentReviewBusy || isConfirmingPaymentComplete || (!!activeParticipantShare && markingParticipantId === activeParticipantShare.participantId);
  // Pay is offered for exactly the shares the pay flow accepts (same filters
  // as getSelectedParticipantIdsForPay).
  const detailIsInr = normalizeCurrency(bill.currency) === "INR";
  const detailPayableIds = getSelectedParticipantIdsForPay(detailParticipantIds);
  const detailCanPay = detailPayableIds.length > 0 && detailPayableIds.length === detailParticipantIds.length;
  const detailPayerParticipant = detailCanPay ? resolvePayerParticipant() : undefined;
  // Payer rule: only a VibeSplit account can receive through this link. A guest or
  // deleted payer gets a plain explanation and the manual Mark as paid path, never
  // the connector review's recipient error.
  const payerOnVibesplit = resolvePayerParticipant()?.type === 'registered';
  const detailPayerHandleMissing =
    detailCanPay &&
    (detailIsInr
      ? !getParticipantUpiId(detailPayerParticipant)
      : !(detailPayerParticipant?.venmoUsername));

  const handoffPerson = (id: string) => {
    const person = bill.participants.find((p) => p.id === id);
    const share = bill.participantShares.find((s) => s.participantId === id);
    // Same structure as the native share detail modal: each item at this person's
    // split share, add-ons as quiet sub-rows, then tax, tip, surcharges and discounts.
    const rows: { label: string; amountLabel: string }[] = [];
    (share?.items || []).forEach((item) => {
      const splitCount = item.assignedTo?.length || 1;
      const quantity = item.quantity && item.quantity > 1 ? ` ×${item.quantity}` : '';
      rows.push({ label: `${item.description || 'Item'}${quantity}${splitCount > 1 ? ` · split ${splitCount} ways` : ''}`, amountLabel: formatMoney(item.amount / splitCount, bill.currency) });
      (item.addons || []).forEach((addon) => rows.push({ label: `+ ${addon.name}${addon.price > 0 ? ` (${formatMoney(addon.price, bill.currency)})` : ''}`, amountLabel: '' }));
    });
    if (share?.taxShare) rows.push({ label: 'Tax', amountLabel: formatMoney(share.taxShare, bill.currency) });
    if (share?.tipShare) rows.push({ label: 'Tip', amountLabel: formatMoney(share.tipShare, bill.currency) });
    Object.entries(share?.surcharges || {}).forEach(([name, amount]) => { if (amount) rows.push({label:formatLabel(name),amountLabel:formatMoney(amount,bill.currency)}); });
    Object.entries(share?.discounts || {}).forEach(([name, amount]) => { if (amount) rows.push({label:formatLabel(name),amountLabel:formatMoney(amount,bill.currency)}); });
    return { label: person?.name || share?.participantName || 'Guest', amountLabel: formatMoney(share?.total || 0, bill.currency), breakdown: rows, status: share?.paymentStatus === 'paid' ? 'Marked as paid' : undefined };
  };
  const paymentPerson = (share: ParticipantShare): BillPaymentPerson => {
    const participant = bill.participants.find((person) => person.id === share.participantId);
    const isPayer = share.participantId === statusSummary.payerId;
    const owesNothing = share.total <= 0;
    const paid = share.paymentStatus === 'paid' || isPayer;
    const awaiting = share.paymentStatus === 'pending_confirmation';
    return {
      id: share.participantId,
      name: share.participantName,
      avatarUrl: participant?.profilePictureUrl,
      amountLabel: formatMoney(share.total, bill.currency),
      disabled: !getSelectedParticipantIdsForPay([share.participantId]).length,
      status: owesNothing ? 'Nothing owed' : isPayer ? 'Bill payer' : paid ? 'Marked as paid' : awaiting ? 'Awaiting confirmation' : undefined,
    };
  };
  // Everything the connector review shows except the receiving methods is already in the
  // payload, so the sheet opens with it (#52). The bill revision travels with each allocation so a
  // payment recorded before the server review arrives still fails closed if the shares changed.
  // Whether the payer can be paid at all is in the payload too (#175), so a payer with no methods
  // opens on the no-methods layout instead of swapping to it when the lookup lands (#181).
  // Supported currencies all use two minor digits.
  const minor = (amount: number) => Math.round(amount * 100);
  const detailReviewPreview = {
    recipientName: payerName,
    recipientUserId: statusSummary.payerId || undefined,
    receivingReady: bill.payerReceiving ? bill.payerReceiving.ready : undefined,
    currency: normalizeCurrency(bill.currency),
    amountMinor: minor(getSelectedTotalForPay(detailParticipantIds)),
    allocations: detailParticipantIds.map((participantId) => ({ billId: bill.id, participantId, expectedRevision: (bill as any).revision || 0, name: handoffPerson(participantId).label, amountMinor: minor(bill.participantShares.find((share) => share.participantId === participantId)?.total || 0), billTitle: bill.title })),
  };
  const detailDraftPayableIds = getSelectedParticipantIdsForPay(detailDraftIds);
  const openDetailPeople = () => {
    setDetailDraftIds(getSelectedParticipantIdsForPay(detailParticipantIds));
    setEditingDetailPeople(true);
  };
  const confirmDetailPeople = () => {
    if (!detailDraftPayableIds.length) return;
    setDetailParticipantIds(detailDraftPayableIds);
    setEditingDetailPeople(false);
  };

  // Venmo handoff interstitial: display data for the pending selection.
  const venmoHandoffPayee = venmoHandoffIds ? resolvePayerParticipant() : undefined;
  const venmoHandoffPayingFor = (venmoHandoffIds || []).map(handoffPerson);

  return (
    <>
    <OpenInAppRow target="b" shareId={shareId} />
    <div className="vs-design bill-page">
      <div className="bill-page__container">
        <header className="bill-page__header">
          <PublicAppHeader showAppLink={false} />
          <button type="button" className="bill-share-trigger" onClick={handleShareBill}>
            <span className="bill-share-trigger__pill"><ShareForwardIcon size={16} aria-hidden="true" /><span>Share bill</span></span>
          </button>
        </header>
        {bill.status === 'pending' && bill.payerReceiving && !bill.payerReceiving.ready && (
          <section className="bill-payer-notice" role="note" aria-label="Link payments unavailable">
            <PayerWarningIcon size={22} aria-hidden="true" />
            <div><strong>{payerName} hasn’t added a payment method</strong><p>{payerName} won’t be able to receive payments until they add a payment method. You can still mark your share as paid.</p></div>
          </section>
        )}
        <main className="bill-layout">
          <section className="bill-overview" aria-label="Bill overview">
            <BillMainDetails title={bill.title} recurring={bill.splitType === 'recurring'} date={bill.date} creator={bill.creator} emoji={bill.emoji}
              receiptImageUrls={bill.receiptImageUrls} showReceipt={showReceipt} setShowReceipt={setShowReceipt}
              currentImageIndex={currentImageIndex} setCurrentImageIndex={setCurrentImageIndex} notes={bill.notes} />
            <div className="bill-overview__summary">
              <div className="bill-overview__total"><span>Total</span><strong>{formatMoney(bill.total, bill.currency)}</strong></div>
              <div className="bill-payer">
                <span className="bill-payer__label">Paid by</span>
                <div className="bill-payer__identity"><PersonAvatar identifier={payer?.id} name={payerName} src={payer?.profilePictureUrl} /><strong>{payerName}</strong></div>
              </div>
            </div>
            {paymentTotals.totalDue > 0 && <div className="bill-progress">
              <div className="bill-progress__labels"><span>{formatMoney(paymentTotals.totalPaid, bill.currency)} marked as paid</span></div>
              <div className="bill-progress__track" role="progressbar" aria-label="Shares marked as paid" aria-valuetext={`${formatMoney(paymentTotals.totalPaid, bill.currency)} marked as paid`} aria-valuenow={Math.round(paymentProgressPercentage)} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${Math.min(100, Math.max(0, paymentProgressPercentage))}%` }} />
              </div>
            </div>}
          </section>
          <div className="bill-detail-column">
            <section className="bill-shares" aria-labelledby="bill-shares-title">
              <div className="bill-section-heading"><h2 id="bill-shares-title">Shares</h2><span>{bill.participantShares.length} {bill.participantShares.length === 1 ? 'person' : 'people'}</span></div>
              {bill.participantShares.some(share => share.total > 0 && share.participantId !== statusSummary.payerId && share.paymentStatus !== 'paid') && <p className="bill-shares-hint"><ArrowDownRight size={18} aria-hidden="true" />Choose your name to review and pay.</p>}
              <SplitRoundingNotice total={bill.total} shares={bill.participantShares.map(share => share.total)} currency={bill.currency} />

              {bill.participantShares.map((share, index) => {
                const participant = bill.participants.find((person) => person.id === share.participantId);
                const isPayer = share.participantId === statusSummary.payerId;
                const owesNothing = !share.total || share.total <= 0;
                const isPaid = share.paymentStatus === 'paid' || isPayer;
                const awaiting = share.paymentStatus === 'pending_confirmation';
                const status = owesNothing ? 'Nothing owed' : isPayer ? 'Bill payer' : isPaid ? 'Marked as paid' : awaiting ? 'Awaiting confirmation' : share.paymentStatus === 'venmo_requested' ? 'Requested' : 'Unpaid';
                return <button type="button" key={share.participantId} className="bill-person-row"
                  aria-label={`${share.participantName}, ${formatMoney(share.total, bill.currency)}, ${status}`}
                  onClick={() => { setActiveParticipantId(share.participantId); setDetailParticipantIds([share.participantId]); setEditingDetailPeople(false); setShowParticipantDetailsModal(true); }}>
                  <PersonAvatar name={share.participantName} src={participant?.profilePictureUrl} identifier={share.participantId} />
                  <span className="bill-person-row__person"><strong>{share.participantName}</strong><span className={isPaid || owesNothing ? 'bill-status--paid' : awaiting ? 'bill-status--pending' : ''}>{(isPaid || owesNothing) && <Check size={13} aria-hidden="true" />}{status}</span></span>
                  <span className="bill-person-row__trailing">
                    <span className="bill-person-row__amount">{formatMoney(share.total, bill.currency)}</span>
                    <span className="bill-person-row__open" aria-hidden="true">Breakdown<ChevronRight size={14} /></span>
                  </span>
                </button>;
              })}
              {bill.participantShares.length === 0 ? <div className="bill-empty"><p>No shares yet.</p><p>Ask the bill creator to add people.</p></div> :
                !hasSelectablePendingShares && <p className="bill-settled"><Check size={18} aria-hidden="true" />{bill.participantShares.some((share) => share.paymentStatus === 'pending_confirmation' && share.participantId !== statusSummary.payerId && share.total > 0) ? 'Payments awaiting confirmation' : 'All shares are settled'}</p>}
            </section>


        {/* Compact Bill Summary - only show if there are items */}
        {bill.items && bill.items.length > 0 && (
          <section className="bill-breakdown" aria-labelledby="bill-breakdown-title">
            <div className="bill-section-heading"><h2 id="bill-breakdown-title">Bill breakdown</h2><span>{bill.items.length} {bill.items.length === 1 ? 'item' : 'items'}</span></div>
            <div className="bill-summary-compact">
            <div className="items-list-compact">
              {bill.items.map((item) => {
                // Get participants assigned to this item
                const assignedParticipants = (item.assignedTo || [])
                  .map((participantId) =>
                    bill.participants.find((p) => p.id === participantId)
                  )
                  .filter(Boolean);

                return (
                  <div key={item.id} className="item-row-compact">
                    <div className="item-left-section">
                      <div className="item-name-with-quantity">
                        <span className="item-name-compact">
                          {item.description || "Unnamed item"}
                        </span>
                        {item.quantity && item.quantity > 1 && (
                          <span className="quantity-badge">
                            ×{item.quantity}
                          </span>
                        )}
                        {assignedParticipants.length > 0 && (
                          <div className="item-avatars">
                            {assignedParticipants
                              .slice(0, FACE_PILE_LIMIT)
                              .map((participant) => {
                                const profilePictureUrl =
                                  participant?.profilePictureUrl;
                                return (
                                  <div
                                    key={participant?.id}
                                    className="item-avatar-tiny" style={avatarStyle(participant?.id || participant?.name || "")}
                                  >
                                    {profilePictureUrl ? (
                                      <img
                                        src={profilePictureUrl}
                                        alt={participant?.name}
                                        className="avatar-img"
                                      />
                                    ) : (
                                      <span className="avatar-text-tiny">
                                        {participant?.name[0].toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            {assignedParticipants.length > FACE_PILE_LIMIT && (
                              <div className="item-avatar-tiny more">
                                <span className="more-text-tiny">
                                  +{assignedParticipants.length - FACE_PILE_LIMIT}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {item.addons && item.addons.length > 0 && (
                        <div className="item-addons">
                          <span className="addon-text">
                            {item.addons
                              .map(
                                (addon) =>
                                  `${addon.name}${
                                    addon.price > 0
                                      ? ` (${formatMoney(addon.price, bill.currency)})`
                                      : ""
                                  }`
                              )
                              .join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="item-amount-compact">
                      {formatMoney(item.amount, bill.currency)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="totals-compact">
              <div className="total-row-compact">
                <span>Subtotal</span>
                <span>{formatMoney(Number(bill.subtotal) || 0, bill.currency)}</span>
              </div>
              {Number(bill.tax) > 0 && (
                <div className="total-row-compact">
                  <span>Tax</span>
                  <span>{formatMoney(Number(bill.tax), bill.currency)}</span>
                </div>
              )}
              {Number(bill.tip) > 0 && (
                <div className="total-row-compact">
                  <span>Tip</span>
                  <span>{formatMoney(Number(bill.tip), bill.currency)}</span>
                </div>
              )}
              {bill.surcharges &&
                Object.entries(bill.surcharges)
                  .filter(([_, value]) => value > 0)
                  .map(([key, value]) => (
                    <div key={key} className="total-row-compact">
                      <span>
                        {key
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </span>
                      <span>{formatMoney(value, bill.currency)}</span>
                    </div>
                  ))}
              {bill.discounts &&
                Object.entries(bill.discounts)
                  .filter(([_, value]) => value !== 0)
                  .map(([key, value]) => (
                    <div key={key} className="total-row-compact">
                      <span>
                        {key
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </span>
                      <span>-{formatMoney(Math.abs(value), bill.currency)}</span>
                    </div>
                  ))}
              <div className="total-row-compact grand-total-compact">
                <span>Total</span>
                <span>{formatMoney(Number(bill.total) || 0, bill.currency)}</span>
              </div>
            </div>
            </div>
          </section>
        )}
          </div>
          <footer className="product-bill-footnotes">
            <ProductNotice>{SPLIT_REMINDER.replace('before sharing', 'before paying')}</ProductNotice>
            <ProductNotice>{PAYMENT_RECORD_NOTICE}</ProductNotice>
          </footer>
        </main>
      </div>

      {activeParticipantShare && <PublicBillDialog className="bill-payment-dialog" open={showParticipantDetailsModal} title={editingDetailPeople ? 'Who are you paying for?' : detailParticipantIds.length > 1 ? 'Your payment' : `${activeParticipantShare.participantName}’s share`} contentHasClose={!editingDetailPeople && !(paymentsV2 && detailCanPay && payerOnVibesplit && !showVenmoDialog)} busy={detailBusy}
        onBack={editingDetailPeople ? () => { setDetailDraftIds([]); setEditingDetailPeople(false); } : undefined}
        onClose={() => { handleCloseVenmoDialog(); setShowParticipantDetailsModal(false); }}>
            {showVenmoDialog ? paymentReturnContent : editingDetailPeople ? <BillPaymentPeople
              people={bill.participantShares.map(paymentPerson).sort((a, b) => Number(a.disabled) - Number(b.disabled))}
              selectedIds={detailDraftPayableIds}
              onToggle={(id) => {
                if (!getSelectedParticipantIdsForPay([id]).length) return;
                setDetailDraftIds((ids) => ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]);
              }}
              totalLabel={formatMoney(getSelectedTotalForPay(detailDraftPayableIds), bill.currency)}
              onContinue={confirmDetailPeople}
              canContinue={detailDraftPayableIds.length > 0}
            /> : paymentsV2 && detailCanPay && payerOnVibesplit ? <PaymentReview onBusy={setPaymentReviewBusy} client={paymentClient} preview={detailReviewPreview} allocations={detailParticipantIds.map(participantId => ({ billId: bill.id, participantId }))} personDetails={(allocation) => ({ status: 'Unpaid', breakdown: handoffPerson(allocation.participantId).breakdown })} onEditPeople={openDetailPeople} onClose={() => setShowParticipantDetailsModal(false)} onRecorded={(recorded) => { celebrate(detailParticipantIds, recorded); setShowParticipantDetailsModal(false); void loadBill(); }} /> : <PayHandoffReminder
              opensInNewTab={!detailIsInr && paymentAppOpensInNewTab("https://venmo.com")}
              title=""
              isPaid={detailPaid && !detailOwesNothing}
              paidHeading={activeParticipantShare.participantId === statusSummary.payerId ? 'Bill payer' : 'Marked as paid'}
              key={JSON.stringify(detailParticipantIds)}
              onEditPeople={detailCanPay && payerOnVibesplit && bill.participantShares.length > 1 ? openDetailPeople : undefined}
              payingFor={detailParticipantIds.map((id) => ({...handoffPerson(id), status: detailParticipantIds.length === 1 ? detailPaid ? 'Marked as paid' : detailAwaiting ? 'Awaiting confirmation' : 'Unpaid' : 'Unpaid'}))}
              amountLabel={formatMoney(detailParticipantIds.length === 1 ? activeParticipantShare.total : getSelectedTotalForPay(detailParticipantIds), bill.currency)}
              payeeName={resolvePayerParticipant()?.name || 'Payer'}
              payeeHandle={detailIsInr ? getParticipantUpiId(resolvePayerParticipant()) : resolvePayerParticipant()?.venmoUsername ? `@${(resolvePayerParticipant()?.venmoUsername || '').replace(/^@/, '')}` : undefined}
              hideContinue={!detailCanPay || !payerOnVibesplit}
              continueDisabled={detailPayerHandleMissing || (detailIsInr && !detailUpiScheme)}
              busy={detailBusy}
              onMarkPaid={detailParticipantIds.length > 1 && detailCanPay ? () => handleConfirmPaymentComplete(detailParticipantIds) : detailCanFlip ? () => detailPaid ? markParticipantAsPending(activeParticipantShare.participantId) : markParticipantAsPaid(activeParticipantShare.participantId) : undefined}
              markPaidLabel={detailParticipantIds.length === 1 && detailPaid ? 'Mark as unpaid' : 'Mark as paid'}
              paymentOptions={<>{detailCanPay && !payerOnVibesplit ? <p className="bill-payment-note">{payerName} isn’t on Vibesplit, so this link can’t take payments. Pay them another way, then mark your share as paid.</p> : detailPayerHandleMissing && <p className="bill-payment-note">{payerName} hasn’t added {detailIsInr ? 'UPI' : 'Venmo'} yet. You can still record a payment below.</p>}{detailIsInr && detailCanPay ? <label className="mb-3 block text-sm text-slate-600">Payment app<select className="mt-1 block w-full rounded-xl border border-slate-200 bg-white p-3" value={detailUpiScheme} onChange={(event) => setDetailUpiScheme(event.target.value)}><option value="">Choose a UPI app</option>{upiAppOptions.map((option) => <option key={option.scheme} value={option.scheme}>{option.label}</option>)}</select></label> : null}</>}
              ctaLabel={detailIsInr ? `Continue to ${upiAppOptions.find((app) => app.scheme === detailUpiScheme)?.label || 'UPI'}` : 'Continue to Venmo'}
              onCancel={() => { handleCloseVenmoDialog(); setShowParticipantDetailsModal(false); }}
              onContinue={() => {
                setSelectedParticipantIds(detailParticipantIds);
                paymentOrigin.current = 'details';
                if (detailIsInr) handlePayWithUpi(detailParticipantIds, detailUpiScheme);
                else return proceedToVenmo(detailParticipantIds);
              }}
            />}

      </PublicBillDialog>}

      <PublicBillDialog open={showShareBillModal} onClose={() => { setShowShareBillModal(false); setShareFeedback(''); setShareFailed(false); }} title="Share bill">
        <div className="bill-share-qr"><QRCodeSVG value={`${window.location.origin}/b/${shareId}`} size={164} bgColor="#ffffff" /><p><strong>Your friends can use this link to pay you back.</strong><span>No Vibesplit app needed.</span></p></div>
        <button type="button" className="vs-button vs-button--primary vs-button--full" disabled={sharing} onClick={handleShareLink}>{mobileShare ? <ShareForwardIcon size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />} {mobileShare ? 'Share link' : 'Copy link'}</button>
        <p className="bill-share-feedback" role="status">{shareFeedback}</p>
        {shareFailed && <input className="bill-share-url" aria-label="Bill link" readOnly value={`${window.location.origin}/b/${shareId}`} onFocus={event => event.currentTarget.select()} />}
        <div className="product-share-footnotes"><ProductNotice>Anyone with this link can view the bill and mark shares as paid or unpaid.</ProductNotice><SharingDetails /></div>
      </PublicBillDialog>

      {pendingUpiPayment && <PublicBillDialog open={showUpiAppPickerModal} title="Pay with UPI" contentHasClose={!!upiHandoffApp} busy={isConfirmingPaymentComplete} onClose={() => { handleCloseVenmoDialog(); }}>
            {upiHandoffApp ? (
              showVenmoDialog ? paymentReturnContent : <PayHandoffReminder onMarkPaid={() => handleConfirmPaymentComplete(pendingUpiPayment.participantIds)} busy={isConfirmingPaymentComplete} key={upiHandoffApp.scheme} payingFor={pendingUpiPayment.participantIds.map(handoffPerson)} amountLabel={formatMoney(pendingUpiPayment.totalAmount, 'INR')} payeeName={pendingUpiPayment.payerName} payeeHandle={pendingUpiPayment.payerUpiId} ctaLabel={`Continue to ${upiHandoffApp.label}`} onCancel={() => { setShowUpiAppPickerModal(false); setPendingUpiPayment(null); }} onContinue={() => { paymentOrigin.current = 'upi'; handleSelectUpiApp(upiHandoffApp.scheme); }} />
            ) : (<>
            {/* Same handoff header as PayHandoffModal — recipient, amount,
                who's covered, and the come-back reminder — reusing its pieces
                so we don't stack a second dialog over the app picker. */}
            <div className="mb-4 flex flex-col gap-4 text-left">
              <PayHandoffRecipient
                name={pendingUpiPayment.payerName}
                handle={pendingUpiPayment.payerUpiId}
                avatarUrl={pendingUpiPayment.payerAvatarUrl}
              />
              <div className="text-3xl font-bold tabular-nums text-slate-900">
                {formatMoney(pendingUpiPayment.totalAmount, "INR")}
              </div>
              <PayHandoffPayingFor payingFor={pendingUpiPayment.participantIds.map(handoffPerson)} />
            </div>
            <p className="upi-app-picker-subtitle">Choose a UPI app to continue.</p>
            <div className="upi-app-picker-list">
              {upiAppOptions.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className="upi-app-picker-button disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setUpiHandoffApp(option)}
                >
                  <span className="upi-app-icon">
                    <img
                      src={option.logoUrl}
                      alt={option.label}
                      className="upi-app-logo-image"
                      loading="lazy"
                    />
                  </span>
                  <span className="upi-app-label">{option.label}</span>
                </button>
              ))}
            </div>
            </>)}
      </PublicBillDialog>}

      {/* Payment review, reading pause, and explicit acknowledgement */}
      <PublicBillDialog open={paymentsV2 && venmoHandoffIds !== null} busy={paymentReviewBusy} title="Payment" onClose={() => setVenmoHandoffIds(null)}>{paymentsV2 && venmoHandoffIds && <PaymentReview onBusy={setPaymentReviewBusy} client={paymentClient} allocations={venmoHandoffIds.map(participantId => ({ billId: bill.id, participantId }))} onClose={() => setVenmoHandoffIds(null)} onRecorded={(recorded) => { celebrate(venmoHandoffIds, recorded); setVenmoHandoffIds(null); void loadBill(); }} />}</PublicBillDialog>
      {/* Settled confirmation: celebrate first, then invite the payer to the app. The content owns its heading and actions. */}
      <PublicBillDialog open={settled !== null} title="Settled" contentHasClose onClose={() => setSettled(null)}>{settled && <SettledCelebration amountLabel={settled.amountLabel} detail={settled.detail} onDone={() => setSettled(null)} />}</PublicBillDialog>
      <PayHandoffModal
        opensInNewTab={paymentAppOpensInNewTab("https://venmo.com")}
        continueDisabled={!(venmoHandoffPayee?.venmoUsername)}
        paymentOptions={!(venmoHandoffPayee?.venmoUsername) ? <p className="bill-payment-note">{payerName} hasn’t added Venmo yet. You can still record a payment below.</p> : undefined}
        open={!paymentsV2 && venmoHandoffIds !== null}
        onCancel={() => { if (!isConfirmingPaymentComplete) handleCloseVenmoDialog(); }}
        confirmation={showVenmoDialog ? paymentReturnContent : undefined}
        busy={isConfirmingPaymentComplete}
        onMarkPaid={() => handleConfirmPaymentComplete(venmoHandoffIds || [])}
        onContinue={handleContinueVenmoHandoff}
        payeeName={venmoHandoffPayee?.name || "the payer"}
        payeeHandle={venmoHandoffPayee?.venmoUsername}
        payeeAvatarUrl={venmoHandoffPayee?.profilePictureUrl}
        amountLabel={formatMoney(
          getSelectedTotalForPay(venmoHandoffIds ?? undefined),
          bill.currency
        )}
        payingFor={venmoHandoffPayingFor}
      />

  </div>
  </>
  );
}
